import React, { useEffect, useMemo, useState } from "react";
import { DataGrid, GridToolbar, GridColDef } from "@material-ui/data-grid";
import { Button } from "@material-ui/core";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { makeGETRequestOnUserService } from "../../Api/api";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { toINR } from "../../Helpers/functions";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
const toastr = require("toastr");

interface ReferralsMoreData {
  user_uuid: string;
  referral_name: string;
  user_signup_date: string;
  order_date: string;
  amount: number;
  type: string;
  order_uuid: string;
  reward_claim: boolean;
}

interface ReferredUser {
  created_at: string;
  kyc_status: string;
  name: string;
  email: string;
  mobile: string;
  lead_owner: string;
  lead_status: string;
  smest_id: string;
  expires_at: string;
  uuid: string;
  user_uuid?: string;
  user_signup_date?: string;
  order_date?: string;
  amount?: number;
  type?: string;
  order_uuid?: string;
  reward_claim?: boolean;
}

interface ReferredUserInitialState {
  referrals: ReferredUser[];
  active_referral_users_count: number;
  invested_referral_count: number;
  reward_earned: number;
  reward_claimed: number;
}

const columns: GridColDef[] = [
  { field: "created_at", headerName: "Created At", width: 180 },
  { field: "kyc_status", headerName: "KYC Status", width: 150 },
  { field: "name", headerName: "Name", width: 200 },
  { field: "email", headerName: "Email", width: 250 },
  { field: "mobile", headerName: "Mobile", width: 150 },
  { field: "lead_owner", headerName: "Lead Owner", width: 200 },
  { field: "lead_status", headerName: "Lead Status", width: 200 },
  { field: "smest_id", headerName: "SMEST ID", width: 200 },
  { field: "user_signup_date", headerName: "Signup Date", width: 180 },
  { field: "order_date", headerName: "Order Date", width: 180 },
  {
    field: "amount",
    headerName: "Amount",
    width: 150,
    valueFormatter: ({ value }) => {
      if (typeof value === "number") {
        return toINR(value, 2);
      }
      return value ?? "-";
    },
  },
  { field: "type", headerName: "Order Type", width: 160 },
  { field: "order_uuid", headerName: "Order UUID", width: 220 },
  {
    field: "reward_claim",
    headerName: "Reward Claimed",
    width: 170,
    valueFormatter: ({ value }) => {
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }
      return value ?? "-";
    },
  },
];

const ReferralsList: React.FC = (props: any) => {
  const referredUserInitianState: ReferredUserInitialState = {
    referrals: [],
    active_referral_users_count: 0,
    invested_referral_count: 0,
    reward_earned: 0,
    reward_claimed: 0,
  };
  const [referredUsers, setReferredUsers] = useState<ReferredUserInitialState>(
    referredUserInitianState
  );
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const currentUserUuid = props?.currentUser?.uuid;

    if (!currentUserUuid) {
      setLoading(false);
      return;
    }

    const fetchReferrals = async () => {
      setLoading(true);

      try {
        const referralsResponse = await makeGETRequestOnUserService(
          `/user/referrals/list/${currentUserUuid}/`
        );

        if (referralsResponse.status !== 200) {
          toastr.error(referralsResponse?.data || "Something went wrong");
          return;
        }

        const referralsData: ReferredUserInitialState = referralsResponse.data;
        let detailsData: ReferralsMoreData[] = [];

        try {
          const detailsResponse = await makeGETRequestOnUserService(
            `/user/referrals/details/${currentUserUuid}/`
          );

          if (
            detailsResponse.status === 200 &&
            Array.isArray(detailsResponse.data)
          ) {
            detailsData = detailsResponse.data;
          } else if (detailsResponse.status !== 200) {
            toastr.error(
              detailsResponse?.data || "Unable to fetch referral details"
            );
          }
        } catch (detailsError: any) {
          const errorMessage =
            detailsError?.message ||
            detailsError ||
            "Unable to fetch referral details";
          toastr.error(errorMessage);
        }

        const detailsByUuid = new Map(
          detailsData
            .filter((item) => item?.user_uuid)
            .map((item) => [item.user_uuid, item])
        );

        const detailsByName = new Map(
          detailsData
            .filter((item) => item?.referral_name)
            .map((item) => [item.referral_name.trim().toLowerCase(), item])
        );

        const mergedReferrals = (referralsData?.referrals ?? []).map(
          (referral: ReferredUser) => {
            // Prefer uuid match; fallback to name for legacy records.
            const primaryMatch = referral.uuid
              ? detailsByUuid.get(referral.uuid)
              : undefined;
            const fallbackMatch = detailsByName.get(
              (referral.name || "").trim().toLowerCase()
            );
            const detail = primaryMatch || fallbackMatch;

            if (!detail) {
              return referral;
            }

            const { referral_name, ...rest } = detail;
            return {
              ...referral,
              ...rest,
            };
          }
        );

        setReferredUsers({
          ...referralsData,
          referrals: mergedReferrals,
        });
      } catch (error: any) {
        const errorMessage = error?.message || error || "Something went wrong";
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, [props?.currentUser?.uuid]);

  const addSerialNumber = (data: ReferredUserInitialState) => {
    return (data?.referrals ?? []).map((row: ReferredUser, index: number) => ({
      ...row,
      id: index + 1,
    }));
  };

  const earningInfo = useMemo(
    () => [
      {
        value: referredUsers?.active_referral_users_count || 0,
        label: "Active Referrals",
      },
      {
        value: referredUsers?.invested_referral_count || 0,
        label: "Referrals Invested",
      },
      {
        value: toINR(referredUsers?.reward_earned, 2) || 0,
        label: "Rewards Earned",
      },
      {
        value: toINR(referredUsers?.reward_claimed, 2) || 0,
        label: "Redeemed",
      },
    ],
    [referredUsers]
  );

  const handleCopyClick = () => {
    const baseUrl =
      process.env.REACT_APP_ENVIRONMENT === "prod"
        ? "https://smest.in"
        : "https://smest.netlify.app";
    const referralUrl = `${baseUrl}/auth/signup?referral_id=${props.currentUser.smestID}`;

    navigator.clipboard.writeText(referralUrl).then(
      () => toastr.success("SMEST ID copied to clipboard!"),
      (err) => toastr.error("Failed to copy!")
    );
  };

  return (
    <div style={{ height: 600, width: "100%" }}>
      <div style={{ marginBottom: "10px" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopyClick}
        >
          Copy SMEST ID
        </Button>
      </div>
      <Box sx={{ my: 4 }}>
        <Grid container spacing={[2, 8]}>
          {earningInfo.map((item, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Box
                sx={{
                  width: "100%",
                  height: 100,
                  borderRadius: 4,
                  overflow: "hidden",
                  p: 2,
                  background: "#F1F1F1",
                  backdropFilter: "blur(35px)",
                  position: "relative",
                }}
              >
                {!!referredUsers ? (
                  <Box
                    sx={{
                      height: "100%",
                      width: "100%",
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: [15, 25],
                        fontWeight: 800,
                        mb: 1,
                      }}
                    >
                      {item.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: [12, 15],
                        fontWeight: 500,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ) : (
                  <Skeleton variant="rounded" width={100} height={40} />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <DataGrid
        pagination
        className="referralsDataGrid"
        components={{ Toolbar: GridToolbar }}
        rows={addSerialNumber(referredUsers)}
        columns={columns}
        loading={loading}
      />
    </div>
  );
};

export default ReferralsList;
